import type { CSSProperties } from "react"
import type { Header, ColumnOrderState } from "@tanstack/react-table"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { flexRender } from "@tanstack/react-table"
import { TableHead } from "@components/ui/table"
import { cn } from "@components/ui/lib/utils"
import type { VariantProps } from "class-variance-authority"
import type { rowVariants } from "@components/ui/table"

interface DraggableTableHeadProps<TData, TValue> {
  header: Header<TData, TValue>
  cellPadding: string
  variant?: VariantProps<typeof rowVariants>["variant"]
  enableColumnResizing: boolean
  columnOrder?: ColumnOrderState
}

export const DraggableTableHead = <TData, TValue>({
  header,
  cellPadding,
  variant,
  enableColumnResizing,
  columnOrder,
}: DraggableTableHeadProps<TData, TValue>) => {
  const isColumnOrderingDisabled =
    !!header.column.columnDef.meta?.disableColumnOrdering
  const isDraggable = !!columnOrder && !isColumnOrderingDisabled

  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.id,
      disabled: !isDraggable,
    })

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: "relative" as const,
    transform: CSS.Translate.toString(transform),
    transition: "width transform 0.2s ease-in-out",
    whiteSpace: "nowrap" as const,
    width: enableColumnResizing ? header.getSize() : undefined,
    zIndex: isDragging ? 1 : 0,
  }

  return (
    <TableHead
      ref={setNodeRef}
      style={style}
      className={cn("tw:font-bold", cellPadding, {
        "tw:cursor-grab": isDraggable && !isDragging,
        "tw:cursor-grabbing": isDraggable && isDragging,
      })}
      colSpan={header.colSpan}
      variant={variant}
      {...attributes}
      {...listeners}
    >
      {header.isPlaceholder
        ? null
        : flexRender(header.column.columnDef.header, header.getContext())}
      {header.column.getCanResize() && (
        <div
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          className={cn(
            "tw:absolute tw:top-0 tw:right-0 tw:h-full tw:w-1",
            "tw:cursor-col-resize tw:bg-gray-300",
            "hover:tw:opacity-100 tw:opacity-0 tw:transition-opacity",
            header.column.getIsResizing() && "tw:bg-primary tw:opacity-100",
          )}
        />
      )}
    </TableHead>
  )
}
